/**
 * Validacao por step do wizard de Plano de Cobranca.
 *
 * As mensagens de erro sao retornadas como objetos `{ key, params? }` com a
 * chave i18n. Isso permite a UI traduzir conforme a lingua selecionada.
 *
 * "Salvar rascunho" NAO valida — gestor pode salvar incompleto.
 * "Salvar e publicar" valida TODOS os steps (validateAll).
 * "Continuar" entre steps valida apenas o step atual.
 */

import type { PlanFormState } from "./types";
import type { WizardStep } from "./types";

export interface ValidationMessage {
  key: string;
  params?: Record<string, string | number>;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationMessage[];
}

const ok: ValidationResult = { valid: true, errors: [] };

function fail(...errors: ValidationMessage[]): ValidationResult {
  return { valid: false, errors };
}

function isPositive(value: string | number | null | undefined): boolean {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? "0"));
  return Number.isFinite(n) && n > 0;
}

export function validateStep(
  step: WizardStep,
  state: PlanFormState,
): ValidationResult {
  switch (step) {
    case 1:
      return validateStep1Onboarding(state);
    case 2:
      return validateStep2Modality(state);
    // Ordem invertida na UI: step 3 = parcelas, step 4 = regras.
    case 3:
      return validateStep4Installments(state);
    case 4:
      return validateStep3Rules(state);
    case 5:
      return validateStep5Fines(state);
    case 6:
      return validateStep6Notifications(state);
    case 7:
      return validateStep7Documents(state);
    default:
      return ok;
  }
}

export function validateAll(state: PlanFormState): ValidationResult {
  const errors: ValidationMessage[] = [];
  for (const step of [1, 2, 3, 4, 5, 6, 7] as WizardStep[]) {
    const res = validateStep(step, state);
    if (!res.valid) {
      errors.push(
        ...res.errors.map((e) => ({
          key: e.key,
          params: { ...(e.params ?? {}), step },
        })),
      );
    }
  }
  return errors.length ? { valid: false, errors } : ok;
}

// ===== Step 1: Dados do cliente =====
// O gestor precisa ter pelo menos UMA secao habilitada para que o cliente
// realmente tenha o que preencher. Documentos/Fotos habilitados precisam
// ter pelo menos 1 item na lista.
function validateStep1Onboarding(state: PlanFormState): ValidationResult {
  const errors: ValidationMessage[] = [];
  const cfg = state.onboardingConfig;

  const algumaHabilitada =
    cfg.dadosPessoais.habilitado ||
    cfg.contatos.habilitado ||
    cfg.referencias.habilitado ||
    cfg.documentos.habilitado ||
    cfg.fotosVideos.habilitado ||
    cfg.chavePix.habilitado;

  if (!algumaHabilitada) {
    errors.push({ key: "wizard.errors.step1.no_section_enabled" });
  }

  if (cfg.documentos.habilitado && cfg.documentos.lista.length === 0) {
    errors.push({ key: "wizard.errors.step1.documents_empty" });
  }

  if (cfg.fotosVideos.habilitado && cfg.fotosVideos.lista.length === 0) {
    errors.push({ key: "wizard.errors.step1.photos_empty" });
  }

  if (cfg.referencias.habilitado) {
    const q = cfg.referencias.quantidade;
    if (!Number.isFinite(q) || q < 1 || q > 5) {
      errors.push({ key: "wizard.errors.step1.references_range" });
    }
  }

  // Flow custom sem loan_simulator SO E problema quando o cliente escolhe
  // o valor. Se tipoValor="fixo" (empresa define), o backend cai no
  // valor_minimo do plano — nao precisa simulador. Nao bloqueia mais
  // fluxos onde a empresa ja fixa o valor (ex: dividas negociadas,
  // planos fechados).
  const isCustomFlow =
    cfg.modeloPronto === false && Array.isArray(cfg.customFlowSteps);
  const clienteEscolheValor = state.tipoValor === "escolhido_cliente";
  if (isCustomFlow && clienteEscolheValor) {
    const steps = (cfg.customFlowSteps || []) as Array<{ type?: string }>;
    const hasSimulator = steps.some((s) => s?.type === "loan_simulator");
    if (!hasSimulator) {
      errors.push({ key: "wizard.errors.step1.custom_flow_needs_simulator" });
    }
  }

  return errors.length ? fail(...errors) : ok;
}

// ===== Step 2: Informacoes basicas + modalidade =====
function validateStep2Modality(state: PlanFormState): ValidationResult {
  const errors: ValidationMessage[] = [];

  if (!state.nome || state.nome.trim().length < 3) {
    errors.push({ key: "wizard.errors.step2.name_required" });
  }

  if (!state.tipoValor) {
    errors.push({ key: "wizard.errors.step2.tipo_valor_required" });
  }

  // Inputs de valor fixo / faixa sao validados na Etapa 3 (onde os inputs estao).
  return errors.length ? fail(...errors) : ok;
}

// ===== Step 3: Regras (inclui valor fixo / faixa min/max/incremento) =====
function validateStep3Rules(state: PlanFormState): ValidationResult {
  const errors: ValidationMessage[] = [];

  // Valor: ambos os modos usam min/max. So "Eu defino" (tipoValor=fixo) usa step.
  if (!isPositive(state.valorMinimo) || !isPositive(state.valorMaximo)) {
    errors.push({ key: "wizard.errors.step2.range_required" });
  } else if (parseFloat(state.valorMaximo) <= parseFloat(state.valorMinimo)) {
    errors.push({ key: "wizard.errors.step2.range_invalid" });
  }
  if (
    state.tipoValor === "fixo" &&
    state.incrementoHabilitado &&
    !isPositive(state.valorStep)
  ) {
    errors.push({ key: "wizard.errors.step2.valor_step_required" });
  }

  if (state.maxActiveCharges < 1 || state.maxActiveCharges > 10) {
    errors.push({ key: "wizard.errors.step3.max_active_range" });
  }

  // "Dias da semana" foi movido pra etapa anterior (parcelas e prazos) — a
  // validacao acompanha em validateStep4Installments.

  if (state.renovacaoHabilitada) {
    const gates: { label: string; value: string }[] = [
      { label: "wizard.errors.step3.gate_min_paid", value: state.renovacaoParcelasMinPagas },
      { label: "wizard.errors.step3.gate_min_days", value: state.renovacaoDiasMin },
      { label: "wizard.errors.step3.gate_max_fines", value: state.renovacaoMultasMax },
      { label: "wizard.errors.step3.gate_open_fines", value: state.renovacaoMultasAbertasMax },
    ];
    for (const g of gates) {
      const n = parseInt(String(g.value ?? ""), 10);
      if (!Number.isFinite(n) || n < 0) {
        errors.push({ key: "wizard.errors.step3.gate_invalid", params: { gateLabel: g.label } });
      }
    }
  }

  return errors.length ? fail(...errors) : ok;
}

// ===== Step 4: Parcelas e prazos =====
// Apos a re-ordenacao da UI, este step tambem inclui "Dias da semana para
// cobrar" (movido de Regras), entao validamos isso aqui.
function validateStep4Installments(state: PlanFormState): ValidationResult {
  const errors: ValidationMessage[] = [];

  if (!state.diasSemana || state.diasSemana.length === 0) {
    errors.push({ key: "wizard.errors.step3.weekdays_required" });
  }

  if (!state.installments || state.installments.length === 0) {
    errors.push({ key: "wizard.errors.step4.no_installment" });
  } else {
    const ativos = state.installments.filter((i) => i.ativo !== false);
    if (ativos.length === 0) {
      errors.push({ key: "wizard.errors.step4.no_active_installment" });
    }
    for (const i of state.installments) {
      if (!i.numParcelas || i.numParcelas < 1) {
        errors.push({
          key: "wizard.errors.step4.num_parcelas_invalid",
          params: { name: i.label || i.tipo },
        });
        break;
      }
      if (i.tipo === "personalizado" && (!i.intervaloDias || i.intervaloDias < 1)) {
        errors.push({
          key: "wizard.errors.step4.intervalo_required",
          params: { name: i.label || i.tipo },
        });
        break;
      }
    }
  }

  return errors.length ? fail(...errors) : ok;
}

// ===== Step 5: Multas e juros =====
function validateStep5Fines(state: PlanFormState): ValidationResult {
  if (!state.tipoMulta) {
    return fail({ key: "wizard.errors.step5.tipo_required" });
  }
  if (state.tipoMulta === "percentual" && !isPositive(state.multaPercentual)) {
    return fail({ key: "wizard.errors.step5.percent_required" });
  }
  if (state.tipoMulta === "valor_fixo" && !isPositive(state.multaValorFixo)) {
    return fail({ key: "wizard.errors.step5.fixed_required" });
  }
  return ok;
}

// ===== Step 6: Notificacoes =====
function validateStep6Notifications(state: PlanFormState): ValidationResult {
  const slots = [
    state.notificacoes.organizacao,
    state.notificacoes.parcela,
    state.notificacoes.ultimo,
  ];
  const ativas = slots.filter((s) => s.ativo);
  if (ativas.length === 0) {
    return fail({ key: "wizard.errors.step6.no_active" });
  }
  for (const s of ativas) {
    if (!s.texto || s.texto.trim().length === 0) {
      return fail({ key: "wizard.errors.step6.empty_text" });
    }
  }
  return ok;
}

// ===== Step 7: Documentos =====
// Apos a refatoracao da tela (cards CTA + opt-out), zero documentos eh valido
// (cliente escolheu "Nao uso documentos por enquanto"). Como o editor de
// conteudo foi removido, o titulo ja vem garantido pelos defaults — nao ha
// mais validacao manual aqui.
function validateStep7Documents(_state: PlanFormState): ValidationResult {
  return ok;
}
